/**
 * UI-3: Operational Investigation Engine
 *
 * Derives investigation context from recent events — NOT full history.
 * This is a decision-support layer, not a data browser.
 *
 * Inputs:  Recent events (time-windowed), entity state (from replay)
 * Outputs: Situation summary, anomalies, impact signals, recommended actions
 */

import type {
  TimelineEvent,
  RoomState,
  GuestState,
  RequestState,
} from './entityEventTypes';
// OPS-38.5.3: action-recommendation derivation expresses operational
// questions ("is this awaiting dispatch?", "is a human actively working
// it?", "is it resolved?") via the canonical axis predicates rather than
// inline `status === 'X'` chains. Atom usage remains where it expresses
// label/translation choices (e.g. choosing "Reassign" vs "Assign" copy),
// per OPS-38.4 §4's boundary rule.
import {
  isRequestActivelyWorked,
  isRequestAwaitingDispatch,
  isRequestResolved,
  isRequestSupervisorAttention,
} from '@/api';

// ─── Investigation Types ────────────────────────────────────────────────────

export type Severity = 'info' | 'warning' | 'critical';

export interface SituationSummary {
  readonly headline: string;
  readonly explanation: string;
  readonly severity: Severity;
  readonly entityLabel: string;
  readonly detectedAt: string | null;
}

export interface Anomaly {
  readonly id: string;
  readonly label: string;
  readonly detail: string;
  readonly timestamp: string;
  readonly severity: Severity;
}

export interface ImpactSignal {
  readonly id: string;
  readonly label: string;
  readonly entityType: 'room' | 'guest' | 'staff' | 'floor';
  readonly entityId: string;
  readonly description: string;
  readonly severity: Severity;
}

export interface RecommendedAction {
  readonly id: string;
  readonly label: string;
  readonly description: string;
  readonly priority: 1 | 2 | 3;           // 1 = highest
  readonly variant: 'primary' | 'secondary' | 'danger';
  readonly actionType: string;             // for programmatic dispatch
}

export interface InvestigationContext {
  readonly situation: SituationSummary;
  readonly recentEvents: readonly TimelineEvent[];   // time-windowed, NOT full history
  readonly anomalies: readonly Anomaly[];
  readonly impactSignals: readonly ImpactSignal[];
  readonly actions: readonly RecommendedAction[];
}

// ─── Time-Windowing ─────────────────────────────────────────────────────────

/**
 * Filter events to a recent time window (default 120 min).
 * Only returns events relevant to the current investigation.
 */
export function windowEvents(
  events: readonly TimelineEvent[],
  windowMinutes = 120,
): readonly TimelineEvent[] {
  const cutoff = Date.now() - windowMinutes * 60 * 1000;
  const windowed = events.filter(
    (e) => new Date(e.timestamp).getTime() >= cutoff,
  );
  // Always include at least the most recent events even if outside window
  if (windowed.length === 0 && events.length > 0) {
    return events.slice(-5);
  }
  return windowed;
}

// ─── Anomaly Detection ──────────────────────────────────────────────────────

function detectAnomalies(events: readonly TimelineEvent[]): Anomaly[] {
  const anomalies: Anomaly[] = [];

  for (const event of events) {
    // Escalations are always anomalies
    if (event.eventType === 'REQUEST_ESCALATED') {
      anomalies.push({
        id: `anomaly-${event.id}`,
        label: 'Request escalated',
        detail: `${event.category ?? 'Request'} in Room ${event.roomNumber} was escalated`,
        timestamp: event.timestamp,
        severity: 'warning',
      });
    }

    // Blocked housekeeping
    if (event.eventType === 'HK_TASK_BLOCKED') {
      anomalies.push({
        id: `anomaly-${event.id}`,
        label: 'Housekeeping blocked',
        detail: `Room ${event.roomNumber} cleaning cannot proceed`,
        timestamp: event.timestamp,
        severity: 'critical',
      });
    }

    // Maintenance reported
    if (event.eventType === 'MAINT_REPORTED') {
      anomalies.push({
        id: `anomaly-${event.id}`,
        label: 'Maintenance issue reported',
        detail: `Room ${event.roomNumber} — ${event.summary ?? event.category ?? 'issue reported'}`,
        timestamp: event.timestamp,
        severity: 'warning',
      });
    }

    // Cancellations
    if (event.eventType === 'STAY_CANCELLED' || event.eventType === 'REQUEST_CANCELLED') {
      anomalies.push({
        id: `anomaly-${event.id}`,
        label: event.eventType === 'STAY_CANCELLED' ? 'Stay cancelled' : 'Request cancelled',
        detail: `Room ${event.roomNumber}${event.guestName ? ' — ' + event.guestName : ''}`,
        timestamp: event.timestamp,
        severity: 'info',
      });
    }

    // Skipped HK
    if (event.eventType === 'HK_TASK_SKIPPED') {
      anomalies.push({
        id: `anomaly-${event.id}`,
        label: 'Housekeeping skipped',
        detail: `Room ${event.roomNumber} cleaning was skipped`,
        timestamp: event.timestamp,
        severity: 'warning',
      });
    }
  }

  return anomalies.sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
  );
}

// ─── Impact Signal Derivation ───────────────────────────────────────────────

function deriveRoomImpactSignals(
  events: readonly TimelineEvent[],
  state: RoomState,
): ImpactSignal[] {
  const signals: ImpactSignal[] = [];

  if (state.hasMaintenanceIssue) {
    signals.push({
      id: 'impact-maint',
      label: 'Active maintenance issue',
      entityType: 'room',
      entityId: state.roomNumber,
      description: 'Room has unresolved maintenance — may affect guest experience',
      severity: 'critical',
    });
  }

  if (state.activeRequestCount > 1) {
    signals.push({
      id: 'impact-multi-req',
      label: `${state.activeRequestCount} open requests`,
      entityType: 'room',
      entityId: state.roomNumber,
      description: 'Multiple concurrent requests may indicate systemic issue',
      severity: 'warning',
    });
  }

  if (state.housekeepingStatus === 'DIRTY' && state.occupancy === 'RESERVED') {
    signals.push({
      id: 'impact-dirty-reserved',
      label: 'Room not ready for arrival',
      entityType: 'room',
      entityId: state.roomNumber,
      description: 'Guest expected but room still needs cleaning',
      severity: 'critical',
    });
  }

  if (state.housekeepingStatus === 'OUT_OF_SERVICE') {
    signals.push({
      id: 'impact-oos',
      label: 'Room out of service',
      entityType: 'room',
      entityId: state.roomNumber,
      description: 'Room is unavailable — capacity reduced',
      severity: 'critical',
    });
  }

  // Floor pattern: check if events mention nearby rooms
  const roomNumbers = new Set(events.map((e) => e.roomNumber).filter(Boolean));
  if (roomNumbers.size > 1) {
    const floor = state.roomNumber.slice(0, -2) || state.roomNumber[0];
    signals.push({
      id: 'impact-floor-activity',
      label: `Activity on floor ${floor ?? '?'}`,
      entityType: 'floor',
      entityId: floor ?? '',
      description: `${roomNumbers.size} rooms with recent activity on same floor`,
      severity: 'info',
    });
  }

  if (state.currentGuestName) {
    const guestEvents = events.filter((e) => e.guestName === state.currentGuestName);
    if (guestEvents.length > 3) {
      signals.push({
        id: 'impact-guest-active',
        label: 'Highly active guest',
        entityType: 'guest',
        entityId: state.currentStayId ?? '',
        description: `${state.currentGuestName} — ${guestEvents.length} interactions in recent window`,
        severity: 'info',
      });
    }
  }

  return signals;
}

function deriveGuestImpactSignals(
  events: readonly TimelineEvent[],
  state: GuestState,
): ImpactSignal[] {
  const signals: ImpactSignal[] = [];

  if (state.openRequests > 0) {
    signals.push({
      id: 'impact-open-requests',
      label: `${state.openRequests} unresolved request${state.openRequests > 1 ? 's' : ''}`,
      entityType: 'guest',
      entityId: state.guestId,
      description: 'Guest has pending requests that need attention',
      severity: state.openRequests > 1 ? 'warning' : 'info',
    });
  }

  if (state.roomNumber) {
    signals.push({
      id: 'impact-room-link',
      label: `Room ${state.roomNumber}`,
      entityType: 'room',
      entityId: state.roomNumber,
      description: 'Currently assigned room — check for related issues',
      severity: 'info',
    });
  }

  // Check for rapid activity (many events in short period)
  if (events.length >= 5) {
    const first = new Date(events[0]!.timestamp).getTime();
    const last = new Date(events[events.length - 1]!.timestamp).getTime();
    const spanMinutes = (last - first) / (1000 * 60);
    if (spanMinutes > 0 && events.length / spanMinutes > 0.1) {
      signals.push({
        id: 'impact-rapid-activity',
        label: 'Unusual activity volume',
        entityType: 'guest',
        entityId: state.guestId,
        description: `${events.length} events in ${Math.round(spanMinutes)} minutes — above normal`,
        severity: 'warning',
      });
    }
  }

  return signals;
}

function deriveRequestImpactSignals(
  events: readonly TimelineEvent[],
  state: RequestState,
): ImpactSignal[] {
  const signals: ImpactSignal[] = [];

  if (state.isOverdue) {
    signals.push({
      id: 'impact-sla-breach',
      label: 'SLA breached',
      entityType: 'room',
      entityId: state.roomNumber ?? '',
      description: 'Request has exceeded its service level deadline',
      severity: 'critical',
    });
  }

  if (state.escalationCount > 0) {
    signals.push({
      id: 'impact-escalated',
      label: `Escalated ${state.escalationCount}×`,
      entityType: 'staff',
      entityId: state.assignedTo ?? '',
      description: 'Request has been escalated — may need supervisor attention',
      severity: 'warning',
    });
  }

  if (state.roomNumber) {
    signals.push({
      id: 'impact-room-link',
      label: `Room ${state.roomNumber}`,
      entityType: 'room',
      entityId: state.roomNumber,
      description: state.guestName
        ? `Affects ${state.guestName} in Room ${state.roomNumber}`
        : `Check room for related issues`,
      severity: 'info',
    });
  }

  // Check if there are other requests from same room in the event set
  const roomEvents = events.filter(
    (e) => e.roomNumber === state.roomNumber && e.entityId !== state.requestId,
  );
  if (roomEvents.length > 0) {
    signals.push({
      id: 'impact-related-requests',
      label: `${roomEvents.length} related event${roomEvents.length > 1 ? 's' : ''}`,
      entityType: 'room',
      entityId: state.roomNumber ?? '',
      description: 'Other activity in same room — may share root cause',
      severity: 'info',
    });
  }

  return signals;
}

// ─── Recommended Actions ────────────────────────────────────────────────────

function deriveRoomActions(state: RoomState): RecommendedAction[] {
  const actions: RecommendedAction[] = [];

  if (state.hasMaintenanceIssue) {
    actions.push({
      id: 'act-escalate-maint',
      label: 'Escalate maintenance',
      description: 'Notify supervisor about unresolved maintenance',
      priority: 1,
      variant: 'danger',
      actionType: 'escalate-maintenance',
    });
  }

  if (state.housekeepingStatus === 'DIRTY' || state.housekeepingStatus === 'OUT_OF_SERVICE') {
    actions.push({
      id: 'act-assign-hk',
      label: 'Assign housekeeping',
      description: 'Priority clean for this room',
      priority: state.occupancy === 'RESERVED' ? 1 : 2,
      variant: state.occupancy === 'RESERVED' ? 'primary' : 'secondary',
      actionType: 'assign-housekeeping',
    });
  }

  if (state.occupancy === 'OCCUPIED' && state.currentGuestName) {
    actions.push({
      id: 'act-contact-guest',
      label: 'Contact guest',
      description: `Reach out to ${state.currentGuestName}`,
      priority: 2,
      variant: 'secondary',
      actionType: 'contact-guest',
    });
  }

  if (state.activeRequestCount > 0) {
    actions.push({
      id: 'act-review-requests',
      label: 'Review open requests',
      description: `${state.activeRequestCount} request${state.activeRequestCount > 1 ? 's' : ''} pending`,
      priority: 2,
      variant: 'secondary',
      actionType: 'review-requests',
    });
  }

  return actions.sort((a, b) => a.priority - b.priority).slice(0, 4);
}

function deriveGuestActions(state: GuestState): RecommendedAction[] {
  const actions: RecommendedAction[] = [];

  if (state.openRequests > 0) {
    actions.push({
      id: 'act-resolve-requests',
      label: 'Review open requests',
      description: `${state.openRequests} pending request${state.openRequests > 1 ? 's' : ''}`,
      priority: 1,
      variant: 'primary',
      actionType: 'review-requests',
    });
  }

  if (state.stayStatus === 'CHECKED_IN') {
    actions.push({
      id: 'act-contact-guest',
      label: 'Contact guest',
      description: `Message ${state.guestName}`,
      priority: 2,
      variant: 'secondary',
      actionType: 'contact-guest',
    });
  }

  if (state.roomNumber) {
    actions.push({
      id: 'act-check-room',
      label: 'Inspect room',
      description: `Check Room ${state.roomNumber} conditions`,
      priority: 3,
      variant: 'secondary',
      actionType: 'inspect-room',
    });
  }

  return actions.sort((a, b) => a.priority - b.priority).slice(0, 4);
}

function deriveRequestActions(state: RequestState): RecommendedAction[] {
  const actions: RecommendedAction[] = [];

  if (isRequestAwaitingDispatch(state.status)) {
    actions.push({
      id: 'act-assign',
      // Atom-driven label is intentional: the operator-facing copy must
      // distinguish "Reassign" (taking over from supervisor attention) from
      // "Assign" (first-touch). This is boundary translation per OPS-38.4 §4.
      label: isRequestSupervisorAttention(state.status) ? 'Reassign request' : 'Assign request',
      description: 'Assign to available staff member',
      priority: 1,
      variant: 'primary',
      actionType: 'assign',
    });
  }

  if (state.isOverdue) {
    actions.push({
      id: 'act-escalate',
      label: 'Escalate to supervisor',
      description: 'SLA breached — needs immediate attention',
      priority: 1,
      variant: 'danger',
      actionType: 'escalate',
    });
  }

  if (isRequestActivelyWorked(state.status)) {
    actions.push({
      id: 'act-resolve',
      label: 'Mark resolved',
      description: 'Confirm issue has been addressed',
      priority: 2,
      variant: 'primary',
      actionType: 'resolve',
    });
  }

  if (state.roomNumber) {
    actions.push({
      id: 'act-check-room',
      label: 'Check room',
      description: `Inspect Room ${state.roomNumber}`,
      priority: 3,
      variant: 'secondary',
      actionType: 'inspect-room',
    });
  }

  return actions.sort((a, b) => a.priority - b.priority).slice(0, 4);
}

// ─── Situation Summary Derivation ───────────────────────────────────────────

function deriveRoomSituation(
  events: readonly TimelineEvent[],
  state: RoomState,
): SituationSummary {
  const entityLabel = `Room ${state.roomNumber}`;

  // Critical: maintenance + occupied
  if (state.hasMaintenanceIssue && state.occupancy === 'OCCUPIED') {
    return {
      headline: `${entityLabel} — Maintenance issue (Critical)`,
      explanation: `Active maintenance issue while ${state.currentGuestName ?? 'guest'} is checked in. Requires immediate attention.`,
      severity: 'critical',
      entityLabel,
      detectedAt: events[events.length - 1]?.timestamp ?? null,
    };
  }

  // Critical: dirty + expected arrival
  if (state.housekeepingStatus === 'DIRTY' && state.occupancy === 'RESERVED') {
    return {
      headline: `${entityLabel} — Not ready for arrival (Critical)`,
      explanation: 'Room needs cleaning before guest check-in. Arrival expected.',
      severity: 'critical',
      entityLabel,
      detectedAt: events[events.length - 1]?.timestamp ?? null,
    };
  }

  // Warning: OOS
  if (state.housekeepingStatus === 'OUT_OF_SERVICE') {
    return {
      headline: `${entityLabel} — Out of service (High)`,
      explanation: 'Room blocked from housekeeping — investigate root cause.',
      severity: 'warning',
      entityLabel,
      detectedAt: events[events.length - 1]?.timestamp ?? null,
    };
  }

  // Warning: multiple open requests
  if (state.activeRequestCount > 1) {
    return {
      headline: `${entityLabel} — Multiple open requests (High)`,
      explanation: `${state.activeRequestCount} concurrent requests may indicate systemic issue.`,
      severity: 'warning',
      entityLabel,
      detectedAt: events[events.length - 1]?.timestamp ?? null,
    };
  }

  // Warning: maintenance (no guest)
  if (state.hasMaintenanceIssue) {
    return {
      headline: `${entityLabel} — Maintenance issue (Medium)`,
      explanation: 'Unresolved maintenance — room may need to be taken offline.',
      severity: 'warning',
      entityLabel,
      detectedAt: events[events.length - 1]?.timestamp ?? null,
    };
  }

  // Info: normal
  return {
    headline: `${entityLabel} — Normal operations`,
    explanation: `${state.occupancy === 'OCCUPIED' ? `${state.currentGuestName ?? 'Guest'} checked in` : state.occupancy === 'RESERVED' ? 'Guest expected' : 'Room vacant'} · Housekeeping: ${state.housekeepingStatus.toLowerCase()}.`,
    severity: 'info',
    entityLabel,
    detectedAt: events[events.length - 1]?.timestamp ?? null,
  };
}

function deriveGuestSituation(
  events: readonly TimelineEvent[],
  state: GuestState,
): SituationSummary {
  const entityLabel = state.guestName !== state.guestId ? state.guestName : `Guest ${state.guestId.slice(0, 8)}`;

  if (state.openRequests > 1) {
    return {
      // OPS-38.5.6: avoid "pending" — it was the overloaded operational
      // word retired by OPS-38.1. "Unresolved" matches the
      // OPERATIONALLY_VISIBLE axis (§3.5) the field actually counts.
      headline: `${entityLabel} — Multiple unresolved requests (High)`,
      explanation: `${state.openRequests} unresolved requests. Guest may be dissatisfied.`,
      severity: 'warning',
      entityLabel,
      detectedAt: events[events.length - 1]?.timestamp ?? null,
    };
  }

  if (state.openRequests === 1) {
    return {
      headline: `${entityLabel} — Unresolved request (Medium)`,
      explanation: `1 unresolved request${state.roomNumber ? ` for Room ${state.roomNumber}` : ''}.`,
      severity: 'warning',
      entityLabel,
      detectedAt: events[events.length - 1]?.timestamp ?? null,
    };
  }

  if (state.stayStatus === 'PRE_ARRIVAL') {
    return {
      headline: `${entityLabel} — Pre-arrival`,
      explanation: `Guest has not checked in yet${state.roomNumber ? `. Room ${state.roomNumber} assigned` : ''}.`,
      severity: 'info',
      entityLabel,
      detectedAt: events[events.length - 1]?.timestamp ?? null,
    };
  }

  return {
    headline: `${entityLabel} — Normal stay`,
    explanation: `${state.stayStatus === 'CHECKED_IN' ? 'Currently checked in' : state.stayStatus === 'CHECKED_OUT' ? 'Checked out' : state.stayStatus}${state.roomNumber ? ` · Room ${state.roomNumber}` : ''}.`,
    severity: 'info',
    entityLabel,
    detectedAt: events[events.length - 1]?.timestamp ?? null,
  };
}

function deriveRequestSituation(
  events: readonly TimelineEvent[],
  state: RequestState,
): SituationSummary {
  const entityLabel = `Request ${state.requestId.slice(0, 8)}`;

  const desc = state.description ? ` — "${state.description}"` : '';

  if (state.isOverdue) {
    return {
      headline: `${entityLabel} — SLA breached (Critical)`,
      explanation: `Request overdue${state.assignedTo ? ` (assigned to ${state.assignedTo})` : ' and unassigned'}${desc}. Immediate action required.`,
      severity: 'critical',
      entityLabel,
      detectedAt: events[events.length - 1]?.timestamp ?? null,
    };
  }

  if (isRequestSupervisorAttention(state.status)) {
    return {
      headline: `${entityLabel} — Escalated (High)`,
      explanation: `Escalated ${state.escalationCount}× — ${state.category ?? 'issue'} in Room ${state.roomNumber ?? '?'}${desc}.`,
      severity: 'warning',
      entityLabel,
      detectedAt: events[events.length - 1]?.timestamp ?? null,
    };
  }

  if (state.status === 'NEW') {
    return {
      headline: `${entityLabel} — Unassigned (Medium)`,
      explanation: `New ${state.category ?? 'request'} from ${state.guestName ?? 'guest'}${state.roomNumber ? ` in Room ${state.roomNumber}` : ''}${desc}. Needs assignment.`,
      severity: 'warning',
      entityLabel,
      detectedAt: events[events.length - 1]?.timestamp ?? null,
    };
  }

  // OPS-38.5.3: COMPLETED is checked via the canonical predicate.
  // CANCELLED is intentionally not part of `isRequestResolved` because
  // it is not a `GuestRequestStatus` enum member — it is set by the
  // cancel route and surfaces here as a `RequestState.status` literal.
  // Keeping the atom check for CANCELLED is boundary translation per §4.
  if (isRequestResolved(state.status) || state.status === 'CANCELLED') {
    return {
      headline: `${entityLabel} — ${isRequestResolved(state.status) ? 'Resolved' : 'Cancelled'}`,
      explanation: `${state.category ?? 'Request'} has been ${state.status.toLowerCase()}${desc}.`,
      severity: 'info',
      entityLabel,
      detectedAt: state.resolvedAt ?? events[events.length - 1]?.timestamp ?? null,
    };
  }

  if (state.status === 'ASSIGNED') {
    return {
      headline: `${entityLabel} — Assigned`,
      explanation: `${state.category ?? 'Request'} assigned to ${state.assignedTo ?? 'staff'}${desc}. Awaiting action.`,
      severity: 'info',
      entityLabel,
      detectedAt: events[events.length - 1]?.timestamp ?? null,
    };
  }

  return {
    headline: `${entityLabel} — In progress`,
    explanation: `${state.category ?? 'Request'}${state.assignedTo ? ` assigned to ${state.assignedTo}` : ''}${desc}.`,
    severity: 'info',
    entityLabel,
    detectedAt: events[events.length - 1]?.timestamp ?? null,
  };
}

// ─── Public API ─────────────────────────────────────────────────────────────

export function buildRoomInvestigation(
  allEvents: readonly TimelineEvent[],
  state: RoomState,
  windowMinutes?: number,
): InvestigationContext {
  const recent = windowEvents(allEvents, windowMinutes);
  return {
    situation: deriveRoomSituation(recent, state),
    recentEvents: recent,
    anomalies: detectAnomalies(recent),
    impactSignals: deriveRoomImpactSignals(recent, state),
    actions: deriveRoomActions(state),
  };
}

export function buildGuestInvestigation(
  allEvents: readonly TimelineEvent[],
  state: GuestState,
  windowMinutes?: number,
): InvestigationContext {
  const recent = windowEvents(allEvents, windowMinutes);
  return {
    situation: deriveGuestSituation(recent, state),
    recentEvents: recent,
    anomalies: detectAnomalies(recent),
    impactSignals: deriveGuestImpactSignals(recent, state),
    actions: deriveGuestActions(state),
  };
}

export function buildRequestInvestigation(
  allEvents: readonly TimelineEvent[],
  state: RequestState,
  windowMinutes?: number,
): InvestigationContext {
  const recent = windowEvents(allEvents, windowMinutes);
  return {
    situation: deriveRequestSituation(recent, state),
    recentEvents: recent,
    anomalies: detectAnomalies(recent),
    impactSignals: deriveRequestImpactSignals(recent, state),
    actions: deriveRequestActions(state),
  };
}
